import React, { useCallback } from 'react';
import { MockupStatus, type MockupStatusState } from './mockupUtils';
import { ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import { CheckmarkIcon, colors } from '@commonsku/styles';

export interface MockupFeedbackBannerProps {
  status: MockupStatusState;
  onFeedback: (isPositive: boolean) => void;
  mockupFeedback: boolean | null;
}

function ErrorBanner({ message }: { message: string | null }): React.ReactElement {
  return (
    <div className="mockup-error-banner" role="alert">
      <span className="error-icon" aria-hidden="true">!</span>
      <span>{message}</span>
    </div>
  );
}

function WarningBanner({
  message,
  onFeedback,
  mockupFeedback,
}: {
  message: string | null;
  onFeedback: (isPositive: boolean) => void;
  mockupFeedback: boolean | null;
}): React.ReactElement {
  const handlePositive = useCallback(() => onFeedback(true), [onFeedback]);
  const handleNegative = useCallback(() => onFeedback(false), [onFeedback]);

  return (
    <>
      <div className="mockup-warning-banner" role="alert">
        <span className="warning-icon" aria-hidden="true">!</span>
        <span>{message}</span>
      </div>
      <div className="mockup-success-feedback" role="status">
        <div className="mockup-success-feedback-message">
          <CheckmarkIcon />
          <span>AI Mockup Configured</span>
        </div>
        <div className="mockup-success-feedback-buttons">
          <button
            type="button"
            className='mockup-success-feedback-button'
            onClick={handlePositive}
            title="Good result"
            aria-label="Thumbs up - good result"
          >
            <ThumbsUpIcon size="24px" color={mockupFeedback === true ? colors.primary1.main : undefined} />
          </button>
          <button
            type="button"
            className='mockup-success-feedback-button'
            onClick={handleNegative}
            title="Poor result"
            aria-label="Thumbs down - poor result"
          >
            <ThumbsDownIcon size="24px" color={mockupFeedback === false ? colors.primary1.main : undefined} />
          </button>
        </div>
      </div>
    </>
  );
}

function SuccessBanner({
  onFeedback,
  mockupFeedback,
}: {
  onFeedback: (isPositive: boolean) => void;
  mockupFeedback: boolean | null;
}): React.ReactElement {
  const handlePositive = useCallback(() => onFeedback(true), [onFeedback]);
  const handleNegative = useCallback(() => onFeedback(false), [onFeedback]);

  return (
    <div className="mockup-success-feedback" role="status">
      <div className="mockup-success-feedback-message">
        <CheckmarkIcon />
        <span>AI Mockup Configured</span>
      </div>
      <div className="mockup-success-feedback-buttons">
        <button
          type="button"
          className='mockup-success-feedback-button'
          onClick={handlePositive}
          title="Good result"
          aria-label="Thumbs up - good result"
        >
          <ThumbsUpIcon size="24px" color={mockupFeedback === true ? colors.primary1.main : undefined} />
        </button>
        <button
          type="button"
          className='mockup-success-feedback-button'
          onClick={handleNegative}
          title="Poor result"
          aria-label="Thumbs down - poor result"
        >
          <ThumbsDownIcon size="24px" color={mockupFeedback === false ? colors.primary1.main : undefined} />
        </button>
      </div>
    </div>
  );
}

export function MockupFeedbackBanner({
  status,
  onFeedback,
  mockupFeedback,
}: MockupFeedbackBannerProps): React.ReactElement | null {
  switch (status.type) {
    case MockupStatus.ERROR:
      return <ErrorBanner message={status.message} />;

    case MockupStatus.WARNING:
      return (
        <WarningBanner message={status.message} onFeedback={onFeedback} mockupFeedback={mockupFeedback} />
      );

    case MockupStatus.SUCCESS:
      return (
        <SuccessBanner onFeedback={onFeedback} mockupFeedback={mockupFeedback} />
      );

    case MockupStatus.IDLE:
    case MockupStatus.PROCESSING:
    default:
      return null;
  }
}
